import {
  Member,
  MemberListFilterByInput,
} from '@tribeplatform/gql-client/types'
import { useStaticIntl } from '@tribeplatform/react-components/i18n'
import { useAuthToken, useMembers } from '@tribeplatform/react-sdk/hooks'
import { simplifyPaginatedResult } from '@tribeplatform/react-sdk/utils'

import { convertToStatic } from '../../Views/utils'
import { staticFieldsFactory } from '../config'
import { useCustomFields } from '../hooks/useCustomFields'
import { useShowMoreButton } from '../hooks/useShowMoreButton'
import type { MembersBlockView } from '../types'
import { getOrderBy, getRoleNameFromFilter } from '../utils/utils'
import { EmptyState } from './EmptyState'
import { GenericMembersList } from './GenericMembersList'
import { LoadingState } from './LoadingState'

interface Props {
  view: MembersBlockView
  activeFieldId: string
}
export const NetworkMemberList = ({
  view: {
    filterBy,
    sort,
    limit,
    style,
    fields,
    gallerySize,
    showDivider,
    showMore,
  },
  activeFieldId,
}: Props) => {
  const staticFields = useStaticIntl(staticFieldsFactory)
  const {
    data: { network },
  } = useAuthToken()
  const { memberFields } = useCustomFields()
  const { orderBy, reverse } = getOrderBy(sort)
  const roleId = network?.roles?.find(
    role => role.name === getRoleNameFromFilter(filterBy),
  )?.id

  const { data, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useMembers({
      fields: {
        profilePicture: 'basic',
        role: 'basic',
        badges: 'default',
        fields: 'basic',
        authMemberProps: 'basic',
      },
      variables: {
        limit,
        orderBy,
        reverse,
        roleIds: roleId ? [roleId] : undefined,
        filterBy:
          (filterBy as MemberListFilterByInput[])?.filter(
            filter => filter?.key !== 'role.name',
          ) ?? [],
      },
      useInfiniteQueryOptions: {
        refetchOnMount: 'always',
      },
    })
  const { nodes: members } = simplifyPaginatedResult<Member>(data)

  const showMoreButton = useShowMoreButton({
    style,
    gallerySize,
    showMore,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  })

  if (!isLoading && data && members && members.length === 0) {
    return <EmptyState />
  }

  if (isLoading || !data) {
    return (
      <LoadingState
        count={limit}
        style={style}
        gallerySize={gallerySize}
        showDivider={showDivider}
      />
    )
  }

  return (
    <>
      <GenericMembersList
        style={style}
        members={members}
        fields={convertToStatic(fields, staticFields, memberFields ?? [])}
        gallerySize={gallerySize}
        showDivider={showDivider}
        activeFieldId={activeFieldId}
        showMoreButton={showMoreButton}
      />
    </>
  )
}
